(ns app.front.view.group.admin.public-map-config
  (:require [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.public-map.model :as public-map-model]
            [app.front.entities :as entities]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.feature-toggle :as feature-toggle]
            [app.front.view.group.admin.public-records.core :as public-records]
            [app.front.view.group.admin.subs :as gas]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::phase-items
  (fn [_ [_ permit-type]]
    (if (= permit-type :permit-type/PI)
      [:workspace.state/periodic-parent
       :workspace.state/execution]
      (->> [(when-not (contains? ptc-model/execution-only-types permit-type)
              :workspace.state/application)
            (when-not (contains? ptc-model/application-only-types permit-type)
              :workspace.state/execution)]
           (filter #{:workspace.state/application :workspace.state/execution})))))

(rf/reg-sub ::root-config-visibility
  (fn [db [_ root-configs]]
    (let [selected (get db ::root-config-visibility)]
      (if selected
        (p/find-first root-configs {:group.config/target selected})
        (first root-configs)))))

(rf/reg-event-db ::set-root-config-visibility
  (fn [db [_ config]]
    (-> (assoc db ::root-config-visibility config)
        (dissoc ::edited-values))))

(rf/reg-sub ::edited-values
  (fn [db [_ attr original-values state]]
    (-> db
        (get ::edited-values)
        (get attr original-values)
        state
        boolean)))

(rf/reg-event-fx ::edit-value
  (fn [{:keys [db]} [_ group-id root-config original-values attr value state]]
    {:db           (if (= value true)
                     (assoc-in db [::edited-values attr] (conj original-values state))
                     (assoc-in db [::edited-values attr] (disj original-values state)))
     :comm/command {:command    :group/set-group-config
                    :method     :post
                    :data       (util/mmap {:config-id        (:db/id root-config)
                                            :attribute-values {attr (if (= value true)
                                                                      (conj original-values state)
                                                                      (disj original-values state))}}
                                           group-id)
                    :notify     {:success :group-admin/public-map-changes-saved}
                    :on-success [::entities/apply-changes]}}))

(defn- checkbox [{:keys [group-id permit-type value root-config original-values label-fn attr]}]
  (cc/checkbox {:id         (str permit-type attr value)
                :label-text (label-fn value)
                :checked    (<sub [::edited-values attr original-values value])
                :on-change  #(>evt [::edit-value group-id root-config original-values attr % value])}))

(defn- checkbox-group [group-id label permit-type values root-config label-fn attr]
  [:div.form-page__input
   [:div.bx--label label]
   [:div.form-page__input-value.form-page__checkbox-group
    (for-frag [value values]
      [checkbox (util/mmap {:original-values (get root-config attr #{})}
                           group-id permit-type value root-config label-fn attr)])]])

(def configurable-ws-attributes
  {:permit-type/B   public-map-model/building-attributes
   :permit-type/BS  public-map-model/building-attributes
   :permit-type/PW  public-map-model/pw-attributes
   :permit-type/PWO public-map-model/pwo-attributes
   :permit-type/P   public-map-model/planning-attributes
   :permit-type/CE  public-map-model/ce-attributes
   :permit-type/LU  public-map-model/lu-attributes
   :permit-type/SW  public-map-model/sw-attributes
   :permit-type/PI  public-map-model/pi-attributes})

(defn workspace-attributes [tr group-id root-config]
  (let [permit-type (:group.config/target root-config)
        label-fn    #(tr (public-map-model/public-map-attribute-tr-key %))
        attributes  (->> (get configurable-ws-attributes permit-type)
                         (sort-by label-fn))]
    [:div.flex-row
     [:h4 (tr [:group-admin/public-map-attributes])]
     [:p (tr [:group-admin/public-map-attributes-info])]
     [:div
      [:div.flex.gap-10
       [:div.flex-col.mt-2
        [checkbox-group
         group-id
         (tr [:group-admin/attributes])
         permit-type
         attributes
         root-config
         label-fn
         :group.config/visible-workspace-attrs-in-public-map]]]]]))

(defn phase-name-key [permit-type phase]
  (if (= permit-type :permit-type/PI)
    (case phase
      :workspace.state/periodic-parent :config-page/periodic-parent
      :workspace.state/execution :config-page/periodic-child)
    (keyword "entity.type" (name phase))))

(defn visible-phase-states [permit-type group-features country states]
  (let [[_ archived-visibility-state] (public-map-model/archived-visibility-config permit-type)]
    (remove #(contains? (cond-> (-> public-map-model/archived-visibility-states
                                    (disj archived-visibility-state)
                                    (conj :state.application/draft))
                          (and (= permit-type :permit-type/P)
                               (not (contains? group-features :group.feature/inspections)))
                          (conj :execution.state/inspections-started)
                          (not (contains? group-features :group.feature/archiving))
                          (conj archived-visibility-state)
                          ;; Upper tier approval is not used by US municipalities and makes this setting confusing.
                          (= country "us")
                          (conj :state.application/in-upper-tier-approval)) %)
            states)))

(defn public-map-root-configs [root-configs permit-types]
  (filter #(contains? permit-types (:group.config/target %)) root-configs))

(defn map-states-and-attributes [tr group permit-types]
  (let [group-id     (:db/id group)
        root-configs (public-map-root-configs (<sub [::gas/root-group-configs]) permit-types)
        phase-states (<sub [::gas/phase-states])
        root-config  (<sub [::root-config-visibility root-configs])
        permit-type  (:group.config/target root-config)]
    [:div.mt-5
     (if (seq (rest root-configs))
       [ui-cs/content-switcher {:items     (map :group.config/target root-configs)
                                :val->str  (comp tr vector)
                                :class     "mb-3"
                                :current   (:group.config/target root-config)
                                :on-change #(>evt [::set-root-config-visibility %])}]
       [:h3.mb-3 (tr [permit-type])])
     [:div.flex.flex-wrap.gap-10
      [:div
       [:h4 (tr [:group-admin/public-map-statuses])]
       [:p (tr [:group-admin/public-map-statuses-info])]
       [:div.flex.gap-10
        (if (empty? (get phase-states permit-type))
          [:div (tr [:group-admin/no-categories])]
          (for-frag [phase (<sub [::phase-items (:group.config/target root-config)])]
            (let [states (->> (get-in phase-states [permit-type phase])
                              (visible-phase-states permit-type
                                                    (:group/features group)
                                                    (<sub [::front-config/country]))
                              (sort-by (comp tr vector)))]
              [:div.flex-col.mt-2
               [checkbox-group
                group-id
                (tr [(phase-name-key permit-type phase)])
                permit-type
                states
                root-config
                #(tr [%])
                (public-map-model/phase-states-conf phase)]])))]]
      [:div
       [workspace-attributes tr group-id root-config]]]]))

(defn public-map-config-section
  [permit-types & [section-options]]
  (let [public-map-permit-types (cond-> public-map-model/permit-types-with-public-map
                                  (not (<sub [::ff/enabled? :feature-flag.id/public-works-public-map]))
                                  (disj :permit-type/PW)
                                  (not (<sub [::ff/enabled? :feature-flag.id/public-work-orders-public-map]))
                                  (disj :permit-type/PWO))]
    (when (and (<sub [::gas/parent-has-feature? :group.feature/domain-public-map])
               (some public-map-permit-types permit-types))
      (let [tr       (<sub [::gas/group-tr])
            group    (<sub [::gas/group])
            group-id (:db/id group)]
        [acommon/config-section
         (tr [:group/public-records-section])
         (tr [:group/public-records-section-description])
         section-options
         [:div.bx--col-xs-12
          (cc/toggle {:id         "public-map-toggle"
                      :label-text (tr [:group/enable-public-records])
                      :toggled    (<sub [::gas/has-feature? :group.feature/public-map])
                      :on-toggle  (fn [checked]
                                    (>evt [::feature-toggle/toggle-group-feature
                                           group-id
                                           :group.feature/public-map
                                           checked
                                           (:entity/version group)]))})]
         (when (contains? (:group/features group) :group.feature/public-map)
           [public-records/public-records-link (:entity/parent group)])
         [map-states-and-attributes tr group public-map-permit-types]]))))
